import React, { useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/ui/password-input";
import { Label } from "@/components/ui/label";
import { ShieldAlert, Loader2, Lock } from "lucide-react";
import { toast } from "sonner";

export const ForcePasswordChange = () => {
  const { user, setUser } = useAuth();
  const [current, setCurrent] = useState("");
  const [pw1, setPw1] = useState("");
  const [pw2, setPw2] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [newPhone, setNewPhone] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pw1.length < 6) return toast.error("Le nouveau mot de passe doit contenir au moins 6 caractères");
    if (pw1 !== pw2) return toast.error("Les deux mots de passe ne correspondent pas");
    setSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: pw1 });
      const updated = { ...user, must_change_password: false };
      // Coordonnées réelles (optionnel) — vérifiées avec le NOUVEAU mot de passe
      if (newEmail.trim() || newPhone.trim()) {
        try {
          const { data } = await api.post("/auth/update-contact", {
            current_password: pw1,
            new_email: newEmail.trim() || null,
            new_phone: newPhone.trim() || null,
          });
          updated.email = data.email;
          updated.phone = data.phone;
          toast.success("Coordonnées mises à jour ✓ (utilisez votre nouvel email pour vous connecter)");
        } catch (err2) {
          toast.error(err2.response?.data?.detail || "Coordonnées non mises à jour — modifiables plus tard");
        }
      }
      localStorage.setItem("mazid_user", JSON.stringify(updated));
      setUser(updated);
      toast.success("Mot de passe changé avec succès ! Bienvenue.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors du changement de mot de passe");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4" data-testid="force-password-change">
      <div className="max-w-md w-full bg-white rounded-2xl border border-slate-200 shadow-sm p-6 sm:p-8">
        <div className="w-14 h-14 rounded-full bg-orange-100 text-[#EA580C] flex items-center justify-center mx-auto mb-4">
          <ShieldAlert className="h-7 w-7" />
        </div>
        <h1 className="font-heading font-extrabold text-2xl text-[#1E3A8A] text-center mb-2">Changez votre mot de passe</h1>
        <p className="text-sm text-slate-600 text-center mb-6">
          Pour sécuriser votre compte, vous devez définir un nouveau mot de passe avant de continuer.
          {user?.matricule && <> Votre mot de passe actuel est votre <strong>matricule</strong> ({user.matricule}).</>}
        </p>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label className="flex items-center gap-1"><Lock className="h-3 w-3" /> Mot de passe actuel *</Label>
            <PasswordInput value={current} onChange={(e) => setCurrent(e.target.value)} placeholder={user?.matricule ? "Votre matricule" : "Mot de passe actuel"} data-testid="fpc-current" required />
          </div>
          <div>
            <Label>Nouveau mot de passe *</Label>
            <PasswordInput value={pw1} onChange={(e) => setPw1(e.target.value)} placeholder="Au moins 6 caractères" data-testid="fpc-new" required minLength={6} />
          </div>
          <div>
            <Label>Confirmez le nouveau mot de passe *</Label>
            <PasswordInput value={pw2} onChange={(e) => setPw2(e.target.value)} placeholder="Retapez le nouveau mot de passe" data-testid="fpc-confirm" required minLength={6} />
          </div>
          <div className="pt-2 border-t border-slate-100">
            <p className="text-xs font-semibold text-slate-500 mb-2">Coordonnées réelles (optionnel) — remplacez l&apos;email/téléphone par défaut :</p>
            <div className="space-y-3">
              <div>
                <Label>Email réel</Label>
                <Input type="email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)}
                  placeholder={user?.email || "votre.email@gmail.com"} data-testid="fpc-email" />
              </div>
              <div>
                <Label>Téléphone réel</Label>
                <Input type="tel" value={newPhone} onChange={(e) => setNewPhone(e.target.value)}
                  placeholder="+224 6XX XX XX XX" data-testid="fpc-phone" />
              </div>
            </div>
          </div>
          <Button type="submit" disabled={saving} className="w-full bg-[#166534] hover:bg-[#14532d] text-white h-11" data-testid="fpc-submit">
            {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : "Valider mon nouveau mot de passe"}
          </Button>
        </form>
      </div>
    </div>
  );
};
